import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { ChatHistorySection } from "./sidebar/ChatHistorySection";
import { UserTypeDisplay } from "./sidebar/UserTypeDisplay";
import { HelpMenu } from "./sidebar/HelpMenu";
import { getRelativeTime } from "./sidebar/utils";
import { useSidebarData } from "@/hooks/useSidebarData";
import { Sidebar, SidebarContent, useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { AccountSettings } from "@/components/sidebar/AccountSettings";
import { PricingSheet } from "@/components/pricing/PricingSheet";

interface AppSidebarProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function AppSidebar({ isOpen, onOpenChange }: AppSidebarProps) {
  const { isExpanded, setIsExpanded } = useSidebar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { userType, chatHistory, avatarUrl, userInitials } = useSidebarData();
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const { user } = useAuth();
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    const chatId = searchParams.get("chat");
    if (chatId) {
      setActiveChat(chatId);
    } else {
      setActiveChat(null);
    }
  }, [searchParams]);

  useEffect(() => {
    setIsExpanded(false);
    onOpenChange?.(false);
  }, []);

  useEffect(() => {
    if (typeof isOpen !== "undefined") {
      setIsExpanded(isOpen);
    }
  }, [isOpen, setIsExpanded]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsExpanded(false);
        onOpenChange?.(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [onOpenChange, setIsExpanded]);

  const handleAnalysisClick = () => {
    setIsExpanded(!isExpanded);
    onOpenChange?.(!isExpanded);
  };

  const handleChatClick = (chatId: string) => {
    if (chatId === activeChat) {
      setIsExpanded(false);
      onOpenChange?.(false);
      return;
    }

    navigate(`/chat?chat=${chatId}`, { replace: false });
    setActiveChat(chatId);
    setIsExpanded(false);
    onOpenChange?.(false);
  };

  const handleNavigation = (path: string) => {
    console.log("Navigating to:", path);
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsExpanded(false);
      onOpenChange?.(false);
    }
  };

  const handleUpgradeClick = () => {
    console.log("Upgrade button clicked");
    setShowPricing(true);
  };

  const recentChats = chatHistory.filter(
    (chat) => getRelativeTime(chat.created_at) === "Recent"
  );

  const last7DaysChats =
    userType !== "admin" && userType !== "pay_per_report"
      ? chatHistory.filter(
          (chat) => getRelativeTime(chat.created_at) === "7 Days"
        )
      : [];

  const olderChats =
    userType !== "admin" && userType !== "pay_per_report"
      ? chatHistory.filter(
          (chat) => getRelativeTime(chat.created_at) === "30 Days"
        )
      : [];

  const renderUserProfileSection = () => {
    if (!user) return null;
    
    if (isExpanded) {
      return (
        <div className="px-4 py-3 border-t border-[#333333] mt-auto">
          <div className="flex items-center justify-between">
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 rounded-lg hover:bg-gray-800/50 transition-colors pl-0"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={avatarUrl || ""} alt="User avatar" />
                    <AvatarFallback className="bg-gray-700 text-sm">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user.email}</span>
                </Button>
              </DialogTrigger>
              <AccountSettings />
            </Dialog>
          </div>
        </div>
      );
    }
    
    return (
      <div className="px-2 py-3 border-t border-[#333333] mt-auto">
        <div className="flex flex-col items-center space-y-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full p-0 hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={avatarUrl || ""} alt="User avatar" />
                  <AvatarFallback className="bg-gray-700 text-sm">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DialogTrigger>
            <AccountSettings />
          </Dialog>
        </div>
      </div>
    );
  };

  return (
    <Sidebar className="bg-[#1A1F2C] border-[#333333]">
      <SidebarContent className="flex flex-col h-full">
        <SidebarHeader
          isExpanded={isExpanded}
          onAnalysisClick={handleAnalysisClick}
        />

        <div className="px-4 py-2">
          <Dialog open={showPricing} onOpenChange={setShowPricing}>
            <DialogTrigger asChild>
              <Button
                onClick={handleUpgradeClick}
                className={isExpanded 
                  ? "w-full rounded-xl flex items-center gap-2 justify-center cursor-pointer bg-sky-500 hover:bg-sky-600 text-white"
                  : "w-full h-10 rounded-lg p-0 flex items-center justify-center hover:bg-gray-800/50 transition-colors text-sky-500"}
                variant={isExpanded ? "default" : "ghost"}
                size={isExpanded ? "default" : "icon"}
                aria-label="Upgrade"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
                  <path d="M12 4L12 20M12 4L18 10M12 4L6 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {isExpanded && <span className="flex-1">Upgrade</span>}
              </Button>
            </DialogTrigger>
            <PricingSheet onClose={() => setShowPricing(false)} />
          </Dialog>
        </div>

        <HelpMenu onNavigate={handleNavigation} isExpanded={isExpanded} />

        {isExpanded && (
          <nav className="flex-1 overflow-y-auto px-3 overscroll-contain text-[#F6F6F7]">
            <UserTypeDisplay userType={userType} />

            {recentChats.length > 0 && (
              <ChatHistorySection
                title={
                  userType === "admin" || userType === "pay_per_report"
                    ? "Last 24 Hours"
                    : "Recent Insights"
                }
                chats={recentChats}
                onChatClick={handleChatClick}
                activeChat={activeChat}
              />
            )}

            {last7DaysChats.length > 0 && (
              <ChatHistorySection
                title="Last 7 Days"
                chats={last7DaysChats}
                onChatClick={handleChatClick}
                activeChat={activeChat}
              />
            )}

            {olderChats.length > 0 && (
              <ChatHistorySection
                title="Last 30 Days"
                chats={olderChats}
                onChatClick={handleChatClick}
                activeChat={activeChat}
              />
            )}
          </nav>
        )}

        {renderUserProfileSection()}
      </SidebarContent>
    </Sidebar>
  );
}
